/**
 * Toasts. Every mutation in this console says out loud whether it worked, because a claim that
 * silently failed is how two reps end up calling the same customer.
 *
 * Radix Toast gives the swipe/timer/pause-on-focus behaviour and, more importantly, announces
 * to a screen reader via a live region.
 *
 * Author: Danish Khan
 */
import * as RToast from '@radix-ui/react-toast';
import clsx from 'clsx';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type ToastTone = 'info' | 'ok' | 'danger';

interface ToastItem {
  id: number;
  title: string;
  body?: string;
  tone: ToastTone;
}

interface ToastApi {
  toast: (title: string, opts?: { body?: string; tone?: ToastTone }) => void;
  /** Convenience for a caught error. */
  toastError: (context: string, err: unknown) => void;
}

const Ctx = createContext<ToastApi | null>(null);

const TONE: Record<ToastTone, string> = {
  info: 'border-line bg-surface text-fg',
  ok: 'border-ok bg-ok-soft text-ok-soft-fg',
  danger: 'border-danger bg-danger-soft text-danger-soft-fg',
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback<ToastApi['toast']>((title, opts) => {
    setItems((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), title, tone: opts?.tone ?? 'info', ...(opts?.body ? { body: opts.body } : {}) },
    ]);
  }, []);

  const toastError = useCallback<ToastApi['toastError']>(
    (context, err) => {
      const message = err instanceof Error ? err.message : String(err);
      setItems((prev) => [
        ...prev,
        { id: Date.now() + Math.random(), title: `Could not ${context}`, body: message, tone: 'danger' },
      ]);
    },
    [],
  );

  const value = useMemo<ToastApi>(() => ({ toast, toastError }), [toast, toastError]);

  return (
    <Ctx.Provider value={value}>
      <RToast.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((item) => (
          <RToast.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((i) => i.id !== item.id));
            }}
            className={clsx(
              'flex items-start gap-3 rounded-[var(--h1-radius)] border p-3 shadow-lg',
              'data-[state=open]:animate-[slide-in_150ms_ease-out]',
              TONE[item.tone],
            )}
          >
            <div className="min-w-0 flex-1">
              <RToast.Title className="text-sm font-semibold">{item.title}</RToast.Title>
              {item.body && <RToast.Description className="mt-0.5 text-xs opacity-90">{item.body}</RToast.Description>}
            </div>
            <RToast.Close className="rounded px-1 text-xs opacity-60 hover:opacity-100" aria-label="Dismiss">
              ✕
            </RToast.Close>
          </RToast.Root>
        ))}
        <RToast.Viewport className="fixed right-4 bottom-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
      </RToast.Provider>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
